import type { FeedbackDto, ReportsDto } from '@nsb/core';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useApi } from '../../api.ts';
import { PageFrame } from '../../components/ui.tsx';
import { isAdmin, isStaff, useMe } from '../../session.tsx';

/**
 * Settings, in sections (Jason, 2026-10-03: "settings should have a left menu
 * secondary that has organizational, process, connections, art, development
 * tools"), each its own page: the process tree a section of its own (managed
 * the way the tree builder did it, since 2026-10-06), and the trades and subs
 * one too. Development tools holds what is about the software itself (Jason,
 * 2026-10-04: "triage is a dumb menu, bugs and feature requests should be
 * exposed in the development tools"): what is running, the bugs and feature
 * requests filed in the portal, and the blog of releases.
 */
export interface SettingsPage {
  label: string;
  to: string;
  /** An admin's alone. */
  admin?: boolean;
}

export interface SettingsSection extends SettingsPage {
  pages?: SettingsPage[];
}

const SECTIONS: SettingsSection[] = [
  { label: 'Organization', to: '/settings/organization' },
  // The tree, managed the way the tree builder did it, an admin's (routes/tree-builder.ts), a section of its own
  // (Jason, 2026-10-06: "Process tree should be a top level setting not process —> process").
  { label: 'Master process tree', to: '/settings/process', admin: true },
  { label: 'Trades and subs', to: '/settings/trades' },
  { label: 'Connections', to: '/settings/connections' },
  { label: 'Art', to: '/settings/art' },
  {
    label: 'Development tools',
    to: '/settings/development',
    pages: [
      { label: 'What is running', to: '/settings/development' },
      { label: 'Bugs and requests', to: '/settings/reports', admin: true },
      { label: 'Development blog', to: '/settings/blog' },
    ],
  },
];

/** What waits on Jason in the reports: a bug not yet looked at, a feature request not yet answered. */
const waiting = (list: FeedbackDto[] | undefined) => (list ?? []).filter((f) => f.kind === 'issue' && f.issueStatus === 'received').length;

const isOn = (p: SettingsPage, pathname: string) => p.to === pathname;
const sectionOn = (s: SettingsSection, pathname: string) => isOn(s, pathname) || (s.pages ?? []).some((p) => isOn(p, pathname));

/**
 * The frame every Settings page sits in: on a wide screen, the five as a
 * column of words beside the page, rows and rules, the current one held by an
 * ink datum and its words; on anything narrower, recomposed as a row of
 * words above it (guide §20.11), never a squeezed sidebar. The column stays
 * narrow so the process tree's drawing keeps its room.
 */
export function SettingsFrame({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  // Settings are NSB's: anyone else finds nothing here, as before.
  if (!isStaff(me)) {
    return (
      <main className="shell pb-28 pt-8 sm:pt-12">
        <p className="py-10 text-ink-2">Nothing here.</p>
      </main>
    );
  }
  return <Framed pathname={pathname} admin={isAdmin(me)}>{children}</Framed>;
}

function Framed({ pathname, admin, children }: { pathname: string; admin: boolean; children: ReactNode }) {
  // The reports waiting to be read show by their name in the menu, so they are seen without a menu of their own.
  const { data: reports } = useApi<ReportsDto>(['feedback', 'reports'], admin ? '/feedback/reports' : null);
  const count = waiting(reports?.reports);
  const sections = SECTIONS.filter((s) => admin || !s.admin).map((s) =>
    s.pages ? { ...s, pages: s.pages.filter((p) => admin || !p.admin).map((p) => (p.to === '/settings/reports' && count ? { ...p, label: `${p.label} · ${count} new` } : p)) } : s,
  );
  return (
    <main className="shell pb-28 pt-8 sm:pt-12">
      <SectionsFrame label="Settings" pathname={pathname} sections={sections}>
        {children}
      </SectionsFrame>
    </main>
  );
}

/**
 * A menu of sections beside the page on a wide screen, a row of words above it on anything narrower, the page in its
 * column: the organization's Settings and a project's alike (Jason, 2026-10-07: "Settings should have that same
 * paradigm that we see in the application level settings").
 */
export function SectionsFrame({ label, pathname, sections, children }: { label: string; pathname: string; sections: SettingsSection[]; children: ReactNode }) {
  const current = sections.find((s) => sectionOn(s, pathname));
  return (
    <div className="xl:grid xl:grid-cols-[9.5rem_minmax(0,1fr)] xl:gap-x-10">
      <SideMenu label={label} pathname={pathname} sections={sections} />
      <RowMenu label={label} pathname={pathname} current={current} sections={sections} />
      <div className="min-w-0">
        <PageFrame>{children}</PageFrame>
      </div>
    </div>
  );
}

/** Wide screens: a column of words, sticky beside the page. */
function SideMenu({ label, pathname, sections }: { label: string; pathname: string; sections: SettingsSection[] }) {
  return (
    <nav aria-label={label} className="hidden xl:block">
      <ul className="sticky top-6 border-t border-rule">
        {sections.map((s) => {
          const on = sectionOn(s, pathname);
          return (
            <li key={s.to} className="border-b border-rule">
              <Link
                to={s.to}
                aria-current={!s.pages && on ? 'page' : undefined}
                className={`label flex min-h-11 items-center border-l-2 pl-3 transition-colors duration-[120ms] ${on ? 'border-ink !text-ink' : 'border-transparent hover:!text-ink'}`}
              >
                {s.label}
              </Link>
              {s.pages ? (
                <ul className="pb-1.5">
                  {s.pages.map((p) => {
                    const here = isOn(p, pathname);
                    return (
                      <li key={p.to}>
                        <Link
                          to={p.to}
                          aria-current={here ? 'page' : undefined}
                          className={`label flex min-h-11 items-center pl-6 transition-colors duration-[120ms] ${here ? '!text-ink underline decoration-ink underline-offset-4' : 'hover:!text-ink'}`}
                        >
                          {p.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Phones and tablets: the five as a row of words above the page; inside a section with pages, its pages as a second row. */
function RowMenu({ label, pathname, current, sections }: { label: string; pathname: string; current: SettingsSection | undefined; sections: SettingsSection[] }) {
  const item = (p: SettingsPage, on: boolean, page: boolean) => (
    <li key={p.to}>
      <Link
        to={p.to}
        aria-current={page && on ? 'page' : undefined}
        className={`label -mb-px flex min-h-11 items-center border-b-2 transition-colors duration-[120ms] ${on ? 'border-ink !text-ink' : 'border-transparent hover:!text-ink'}`}
      >
        {p.label}
      </Link>
    </li>
  );
  return (
    <nav aria-label={label} className="mb-8 xl:hidden">
      <ul className="flex flex-wrap gap-x-6 border-b border-rule">{sections.map((s) => item(s, sectionOn(s, pathname), !s.pages))}</ul>
      {current?.pages ? <ul className="flex flex-wrap gap-x-6 border-b border-rule">{current.pages.map((p) => item(p, isOn(p, pathname), true))}</ul> : null}
    </nav>
  );
}
